import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const CountUp = ({
  end,
  suffix = '',
  duration = 2.5,
  inView,
}: {
  end: number;
  suffix?: string;
  duration?: number;
  inView: boolean;
}) => {
  const [count, setCount] = useState(0);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!inView || hasAnimated.current) return;
    hasAnimated.current = true;
    const startTime = Date.now();
    const isDecimal = end % 1 !== 0;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * end;
      setCount(isDecimal ? parseFloat(current.toFixed(1)) : Math.floor(current));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [inView, end, duration]);

  return (
    <span>
      {end % 1 !== 0 ? count.toFixed(1) : count.toLocaleString('tr-TR')}
      {suffix}
    </span>
  );
};

const ProductionQualityPage = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const [isLoaded, setIsLoaded] = useState(false);

  const bgX = useTransform(mouseX, [0, 1], [-8, 8]);
  const bgY = useTransform(mouseY, [0, 1], [-8, 8]);

  const [refFeatures, inViewFeatures] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [refProcess, inViewProcess] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [refQuality, inViewQuality] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [refLab, inViewLab] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [refCTA, inViewCTA] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      mouseX.set((e.clientX - rect.left) / rect.width);
      mouseY.set((e.clientY - rect.top) / rect.height);
    },
    [mouseX, mouseY]
  );

  const heroStats = [
    { number: 4000, suffix: '+', label: 'Günlük Üretim', icon: 'ri-settings-3-line' },
    { number: 99.8, suffix: '%', label: 'Kalite Skoru', icon: 'ri-shield-star-line' },
    { number: 25, suffix: '+', label: 'Yıllık Tecrübe', icon: 'ri-time-line' },
  ];

  const productionFeatures = [
    {
      icon: 'ri-settings-3-line',
      title: 'Entegre Üretim',
      description: 'Kalıp tasarımından paketlemeye tüm süreçler tek çatı altında',
      color: 'from-teal to-green',
    },
    {
      icon: 'ri-tools-line',
      title: 'CNC Kalıp Atölyesi',
      description: 'Hızlı ürün geliştirme ve mikron hassasiyetinde kalıp üretimi',
      color: 'from-green to-teal',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Gıda Uygunluğu',
      description: 'FDA ve AB standartlarına uygun hammadde ve prosesler',
      color: 'from-teal to-green',
    },
    {
      icon: 'ri-microscope-line',
      title: 'Kalite Laboratuvarı',
      description: 'Her partide detaylı fiziksel ve kimyasal testler',
      color: 'from-green to-teal',
    },
  ];

  const productionSteps = [
    {
      number: '01',
      title: 'Kalıp Tasarımı',
      desc: 'CNC tezgâhlarında mikron hassasiyetinde kalıp işleme',
      icon: 'ri-pencil-ruler-2-line',
    },
    {
      number: '02',
      title: 'Hammadde Kabul',
      desc: 'Gıda uygunluk kriterleriyle parti bazlı izlenebilirlik',
      icon: 'ri-truck-line',
    },
    {
      number: '03',
      title: 'Şişirme Üretim',
      desc: 'Optimize proses parametreleriyle stabil üretim',
      icon: 'ri-settings-3-line',
    },
    {
      number: '04',
      title: 'Ara Kontroller',
      desc: 'Gramaj, boyut ve ağız uyumu tolerans kontrolü',
      icon: 'ri-search-eye-line',
    },
    {
      number: '05',
      title: 'Son Kontrol',
      desc: 'Görsel ve fonksiyonel kalite denetimi',
      icon: 'ri-checkbox-circle-line',
    },
    {
      number: '06',
      title: 'Paketleme',
      desc: 'Hijyenik ortamda güvenli sevkiyat hazırlığı',
      icon: 'ri-box-3-line',
    },
  ];

  const qualityControls = [
    { icon: 'ri-ruler-line', title: 'Boyutsal Ölçüm', desc: 'Tolerans ve boyut kontrolü' },
    { icon: 'ri-scales-3-line', title: 'Gramaj Kontrolü', desc: 'Ağırlık ve kalınlık ölçümü' },
    { icon: 'ri-drop-line', title: 'Sızdırmazlık', desc: 'Kapak performans testleri' },
    { icon: 'ri-eye-line', title: 'Görsel Kontrol', desc: 'Yüzey kalite incelemeleri' },
    { icon: 'ri-archive-line', title: 'Numune Saklama', desc: 'Parti bazlı arşivleme' },
    { icon: 'ri-file-list-3-line', title: 'Proses İzleme', desc: 'Süreklilik takip sistemi' },
  ];

  const labTests = [
    { title: 'Fiziksel Testler', desc: 'Boyut, ağırlık, mukavemet ölçümleri', icon: 'ri-ruler-2-line' },
    { title: 'Kimyasal Analizler', desc: 'Migrasyon ve uygunluk testleri', icon: 'ri-flask-line' },
    { title: 'Sızdırmazlık Testleri', desc: 'Vakum dayanımı ve kapak performansı', icon: 'ri-drop-line' },
    { title: 'Görsel Kontroller', desc: 'Berraklık, renk, yüzey kalitesi', icon: 'ri-eye-line' },
  ];

  return (
    <div className="min-h-screen bg-white font-poppins">
      <Navbar />

      {/* Hero Section */}
      <section
        ref={containerRef}
        onMouseMove={handleMouseMove}
        className="relative min-h-screen flex items-center overflow-hidden"
      >
        {/* Background Image with Parallax */}
        <motion.div style={{ x: bgX, y: bgY }} className="absolute inset-[-20px]">
          <img
            src="https://public.readdy.ai/ai/img_res/edited_c991be5cb81ae7e704519a171ea8115d_d5244f4e.jpg"
            alt="AnadoluPet Üretim Tesisi"
            className="w-full h-full object-cover object-center scale-105"
          />
        </motion.div>

        {/* Dark Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/85 to-navy/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-transparent to-navy/40" />

        {/* Soft Glowing Orbs */}
        <motion.div
          animate={{
            x: [0, 15, -10, 0],
            y: [0, -10, 15, 0],
            scale: [1, 1.08, 0.95, 1],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-[15%] right-[20%] w-72 h-72 bg-green/8 rounded-full blur-[120px] z-[1]"
        />
        <motion.div
          animate={{
            x: [0, -20, 10, 0],
            y: [0, 15, -10, 0],
            scale: [1, 0.92, 1.05, 1],
          }}
          transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-[20%] left-[10%] w-96 h-96 bg-teal/6 rounded-full blur-[140px] z-[1]"
        />

        {/* Main Content */}
        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-28 pb-12 sm:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center min-h-[calc(100vh-160px)]">
            {/* Left Content */}
            <div className="lg:col-span-7 xl:col-span-7">
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={isLoaded ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
                className="mb-6 sm:mb-8"
              >
                <span className="inline-flex items-center gap-2.5 px-5 py-2.5 bg-white/10 border border-white/20 rounded-full backdrop-blur-md">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green" />
                  </span>
                  <span className="text-white/90 text-xs sm:text-sm font-medium tracking-wide">
                    ISO 9001 & ISO 22000 Sertifikalı Tesis
                  </span>
                </span>
              </motion.div>

              {/* Title */}
              <div className="mb-6 sm:mb-8 overflow-hidden">
                <motion.h1
                  initial={{ opacity: 0, y: 40 }}
                  animate={isLoaded ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-2"
                >
                  Üretimde Mükemmellik,
                </motion.h1>
                <motion.h1
                  initial={{ opacity: 0, y: 40 }}
                  animate={isLoaded ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl font-bold leading-[1.1]"
                >
                  <span className="bg-gradient-to-r from-green via-teal to-green bg-clip-text text-transparent">
                    Kalitede Liderlik
                  </span>
                </motion.h1>
              </div>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 15 }}
                animate={isLoaded ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.5, ease: 'easeOut' }}
                className="text-base sm:text-lg text-white/80 mb-8 sm:mb-10 max-w-xl leading-relaxed"
              >
                Kalıp tasarımından paketlemeye kadar tüm süreçler tek çatı altında. 
                Entegre üretim altyapımız ile uluslararası standartlarda ambalaj üretimi.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isLoaded ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.6, ease: 'easeOut' }}
                className="flex flex-col sm:flex-row items-start gap-4 mb-12 sm:mb-16"
              >
                <a
                  href="#uretim-sureci"
                  className="group relative px-8 sm:px-10 py-3.5 sm:py-4 bg-gradient-to-r from-green to-teal text-white text-sm sm:text-base font-semibold rounded-lg overflow-hidden transition-all duration-500 shadow-lg shadow-green/25 hover:shadow-xl hover:shadow-green/30 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    Üretim Sürecimiz
                    <motion.i
                      animate={{ y: [0, 3, 0] }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                      className="ri-arrow-down-line text-lg"
                    />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-teal to-green opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </a>
                <a
                  href="#kalite-kontrol"
                  className="group px-8 sm:px-10 py-3.5 sm:py-4 bg-white/5 border border-white/30 text-white text-sm sm:text-base font-semibold rounded-lg hover:bg-white/15 hover:border-white/50 hover:-translate-y-0.5 transition-all duration-500 whitespace-nowrap cursor-pointer backdrop-blur-sm"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-shield-check-line text-green" />
                    Kalite Standartları
                  </span>
                </a>
              </motion.div>

              {/* Stats Row */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isLoaded ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.7, ease: 'easeOut' }}
                className="flex flex-wrap gap-4 sm:gap-6 lg:gap-8"
              >
                {heroStats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={isLoaded ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.6, delay: 0.8 + index * 0.1, ease: 'easeOut' }}
                    className="group flex items-center gap-3 sm:gap-4 bg-white/5 backdrop-blur-md border border-white/10 rounded-xl px-5 sm:px-6 py-3.5 sm:py-4 hover:bg-white/10 hover:border-green/30 transition-all duration-500 cursor-default"
                  >
                    <div className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center bg-green/20 rounded-lg group-hover:bg-green/30 transition-colors duration-500">
                      <i className={`${stat.icon} text-green text-lg sm:text-xl`} />
                    </div>
                    <div>
                      <div className="text-xl sm:text-2xl font-bold text-white">
                        <CountUp end={stat.number} suffix={stat.suffix} inView={isLoaded} />
                      </div>
                      <div className="text-white/60 text-xs sm:text-sm font-medium">{stat.label}</div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Right Side - Dramatically Animated Cards */}
            <div className="hidden lg:flex lg:col-span-5 xl:col-span-5 relative items-center justify-center">
              <div className="relative w-full h-[520px]">

                {/* Rotating outer rings */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{ border: '1px dashed rgba(52,211,153,0.18)' }}
                />
                <motion.div
                  animate={{ rotate: -360 }}
                  transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[440px] h-[440px] rounded-full pointer-events-none"
                  style={{ border: '1px dashed rgba(20,184,166,0.10)' }}
                />

                {/* Pulsing center glow */}
                <motion.div
                  animate={{ scale: [1, 1.4, 1], opacity: [0.15, 0.35, 0.15] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-green/20 rounded-full blur-[80px] pointer-events-none"
                />

                {/* Orbiting dot on outer ring */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px] h-[340px] pointer-events-none"
                >
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-green rounded-full shadow-[0_0_12px_4px_rgba(52,211,153,0.6)]" />
                </motion.div>
                <motion.div
                  animate={{ rotate: -360 }}
                  transition={{ duration: 14, repeat: Infinity, ease: 'linear' }}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[440px] h-[440px] pointer-events-none"
                >
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-teal/80 rounded-full shadow-[0_0_8px_3px_rgba(20,184,166,0.5)]" />
                </motion.div>

                {/* Center Icon with 3D bob */}
                <motion.div
                  initial={{ opacity: 0, scale: 0, rotateY: -90 }}
                  animate={isLoaded ? { opacity: 1, scale: 1, rotateY: 0 } : {}}
                  transition={{ duration: 1, delay: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20"
                >
                  <motion.div
                    animate={{
                      y: [0, -6, 0],
                      rotateZ: [0, 4, -4, 0],
                      boxShadow: [
                        '0 8px 32px rgba(52,211,153,0.3)',
                        '0 20px 48px rgba(52,211,153,0.5)',
                        '0 8px 32px rgba(52,211,153,0.3)',
                      ],
                    }}
                    transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                    className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-green to-teal rounded-2xl cursor-pointer"
                  >
                    <i className="ri-settings-3-line text-white text-4xl" />
                  </motion.div>
                </motion.div>

                {/* Card 1 — CNC Kalıp (top-right) */}
                <motion.div
                  initial={{ opacity: 0, x: 80, y: -40, rotateY: 45, scale: 0.7 }}
                  animate={isLoaded ? { opacity: 1, x: 0, y: 0, rotateY: 0, scale: 1 } : {}}
                  transition={{ duration: 1, delay: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
                  className="absolute right-[4%] top-[8%] z-10"
                  style={{ perspective: 800 }}
                >
                  <motion.div
                    animate={{ y: [0, -10, 0], rotateZ: [0, 1.5, -1.5, 0] }}
                    transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                    whileHover={{ scale: 1.08, rotateY: -8, rotateX: 6, z: 30 }}
                    className="group relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-5 cursor-pointer shadow-2xl shadow-black/20 min-w-[170px] overflow-hidden"
                    style={{ transformStyle: 'preserve-3d' }}
                  >
                    {/* Animated gradient border */}
                    <motion.div
                      animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
                      transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                      style={{
                        background: 'linear-gradient(90deg, rgba(52,211,153,0.4), rgba(20,184,166,0.4), rgba(52,211,153,0.4))',
                        backgroundSize: '200% 100%',
                        padding: '1px',
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                      }}
                    />
                    {/* Shimmer sweep */}
                    <motion.div
                      animate={{ x: ['-120%', '220%'] }}
                      transition={{ duration: 2.2, repeat: Infinity, repeatDelay: 1.5, ease: 'easeInOut' }}
                      className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/15 to-transparent skew-x-12 pointer-events-none"
                    />
                    <motion.div
                      whileHover={{ scale: 1.15, rotate: 12 }}
                      transition={{ duration: 0.35 }}
                      className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-green/40 to-teal/40 rounded-xl mb-3 group-hover:from-green/60 group-hover:to-teal/60 transition-colors duration-300"
                    >
                      <i className="ri-tools-line text-green text-2xl" />
                    </motion.div>
                    <div className="text-white font-bold text-sm mb-1">CNC Kalıp</div>
                    <div className="text-white/50 text-xs mb-3">±0.01mm hassasiyet</div>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={isLoaded ? { width: '75%' } : {}}
                      transition={{ duration: 1.2, delay: 1.2 }}
                      className="h-0.5 bg-gradient-to-r from-green to-teal rounded-full mb-2"
                    />
                    <div className="flex items-center gap-1 text-green/80 text-xs font-semibold">
                      <span>Detaylar</span>
                      <motion.i
                        animate={{ x: [0, 4, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="ri-arrow-right-s-line"
                      />
                    </div>
                    {/* Corner accent */}
                    <div className="absolute top-0 right-0 w-8 h-8 overflow-hidden rounded-tr-2xl">
                      <div className="absolute top-0 right-0 w-0 h-0 border-l-[32px] border-l-transparent border-t-[32px] border-t-green/30" />
                    </div>
                  </motion.div>
                </motion.div>

                {/* Card 2 — Kalite Kontrol (middle-right, larger) */}
                <motion.div
                  initial={{ opacity: 0, x: 100, rotateY: 60, scale: 0.6 }}
                  animate={isLoaded ? { opacity: 1, x: 0, rotateY: 0, scale: 1 } : {}}
                  transition={{ duration: 1.1, delay: 0.55, ease: [0.34, 1.56, 0.64, 1] }}
                  className="absolute right-[-2%] top-[42%] -translate-y-1/2 z-20"
                  style={{ perspective: 800 }}
                >
                  <motion.div
                    animate={{ y: [0, -14, 0], rotateZ: [0, -1.5, 1.5, 0] }}
                    transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
                    whileHover={{ scale: 1.1, rotateY: 10, rotateX: -5, z: 40 }}
                    className="group relative bg-gradient-to-br from-green/20 to-teal/15 backdrop-blur-xl border border-green/30 rounded-2xl p-6 cursor-pointer shadow-2xl shadow-green/20 min-w-[190px] overflow-hidden"
                    style={{ transformStyle: 'preserve-3d' }}
                  >
                    {/* Pulsing glow behind card */}
                    <motion.div
                      animate={{ opacity: [0.3, 0.7, 0.3], scale: [0.9, 1.1, 0.9] }}
                      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                      className="absolute inset-0 bg-green/10 rounded-2xl blur-xl pointer-events-none"
                    />
                    {/* Shimmer sweep */}
                    <motion.div
                      animate={{ x: ['-120%', '220%'] }}
                      transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 2, ease: 'easeInOut' }}
                      className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 pointer-events-none"
                    />
                    <div className="relative">
                      <motion.div
                        animate={{ rotate: [0, 360] }}
                        transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
                        whileHover={{ scale: 1.2 }}
                        className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-green/50 to-teal/50 rounded-xl mb-4 shadow-lg shadow-green/30"
                      >
                        <i className="ri-shield-check-line text-white text-3xl" />
                      </motion.div>
                      <div className="text-white font-bold text-base mb-1">Kalite Kontrol</div>
                      <div className="text-white/60 text-xs mb-4">Her partide tam denetim</div>
                      {/* Animated stat bar */}
                      <div className="space-y-2">
                        {[
                          { label: 'Başarı Oranı', val: 99.8, w: '99%' },
                          { label: 'Test Kapsamı', val: 100, w: '100%' },
                        ].map((bar, bi) => (
                          <div key={bi}>
                            <div className="flex justify-between text-xs text-white/60 mb-1">
                              <span>{bar.label}</span>
                              <span className="text-green font-semibold">{bar.val}%</span>
                            </div>
                            <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={isLoaded ? { width: bar.w } : {}}
                                transition={{ duration: 1.5, delay: 1.4 + bi * 0.2, ease: 'easeOut' }}
                                className="h-full bg-gradient-to-r from-green to-teal rounded-full"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Animated border glow */}
                    <motion.div
                      animate={{ opacity: [0, 1, 0] }}
                      transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                      className="absolute inset-0 rounded-2xl border border-green/60 pointer-events-none"
                    />
                  </motion.div>
                </motion.div>

                {/* Card 3 — Laboratuvar (bottom-right) */}
                <motion.div
                  initial={{ opacity: 0, x: 60, y: 50, rotateY: -45, scale: 0.7 }}
                  animate={isLoaded ? { opacity: 1, x: 0, y: 0, rotateY: 0, scale: 1 } : {}}
                  transition={{ duration: 1, delay: 0.8, ease: [0.34, 1.56, 0.64, 1] }}
                  className="absolute right-[6%] bottom-[6%] z-10"
                  style={{ perspective: 800 }}
                >
                  <motion.div
                    animate={{ y: [0, -8, 0], rotateZ: [0, 2, -2, 0] }}
                    transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
                    whileHover={{ scale: 1.08, rotateY: 8, rotateX: 5, z: 30 }}
                    className="group relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-5 cursor-pointer shadow-2xl shadow-black/20 min-w-[170px] overflow-hidden"
                    style={{ transformStyle: 'preserve-3d' }}
                  >
                    {/* Shimmer sweep */}
                    <motion.div
                      animate={{ x: ['-120%', '220%'] }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 2.5, ease: 'easeInOut' }}
                      className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/15 to-transparent skew-x-12 pointer-events-none"
                    />
                    {/* Particle dots */}
                    {[0, 1, 2].map((p) => (
                      <motion.div
                        key={p}
                        animate={{
                          y: [0, -20, 0],
                          opacity: [0, 1, 0],
                          x: [0, (p - 1) * 8, 0],
                        }}
                        transition={{
                          duration: 2.5,
                          repeat: Infinity,
                          delay: p * 0.6,
                          ease: 'easeOut',
                        }}
                        className="absolute bottom-3 w-1 h-1 bg-teal/60 rounded-full pointer-events-none"
                        style={{ left: `${30 + p * 20}%` }}
                      />
                    ))}
                    <motion.div
                      whileHover={{ scale: 1.15, rotate: -10 }}
                      transition={{ duration: 0.35 }}
                      className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-teal/40 to-green/40 rounded-xl mb-3 group-hover:from-teal/60 group-hover:to-green/60 transition-colors duration-300"
                    >
                      <i className="ri-microscope-line text-teal text-2xl" />
                    </motion.div>
                    <div className="text-white font-bold text-sm mb-1">Laboratuvar</div>
                    <div className="text-white/50 text-xs mb-3">Fiziksel &amp; kimyasal test</div>
                    {/* Animated dots indicator */}
                    <div className="flex items-center gap-1.5">
                      {[0, 1, 2, 3].map((d) => (
                        <motion.div
                          key={d}
                          animate={{ scale: [1, 1.5, 1], opacity: [0.4, 1, 0.4] }}
                          transition={{ duration: 1.5, repeat: Infinity, delay: d * 0.3 }}
                          className="w-1.5 h-1.5 bg-teal rounded-full"
                        />
                      ))}
                      <span className="text-teal/80 text-xs font-semibold ml-1">Aktif</span>
                    </div>
                    {/* Corner accent */}
                    <div className="absolute top-0 right-0 w-8 h-8 overflow-hidden rounded-tr-2xl">
                      <div className="absolute top-0 right-0 w-0 h-0 border-l-[32px] border-l-transparent border-t-[32px] border-t-teal/30" />
                    </div>
                  </motion.div>
                </motion.div>

              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.5 }}
          className="absolute bottom-8 sm:bottom-12 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        >
          <motion.div
            animate={{ y: [0, 5, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="w-6 h-10 border-2 border-white/20 rounded-full flex items-start justify-center p-1.5"
          >
            <motion.div
              animate={{ y: [0, 10, 0], opacity: [1, 0.4, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              className="w-1.5 h-1.5 bg-green rounded-full"
            />
          </motion.div>
        </motion.div>
      </section>

      {/* Production Features */}
      <section ref={refFeatures} className="py-20 lg:py-28 bg-white relative overflow-hidden">
        {/* Background Animation */}
        <motion.div
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-20 left-10 w-96 h-96 bg-teal/5 rounded-full blur-3xl pointer-events-none"
        />
        <motion.div
          animate={{ x: [0, -20, 0], y: [0, 30, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-20 right-10 w-80 h-80 bg-green/5 rounded-full blur-3xl pointer-events-none"
        />

        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inViewFeatures ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-14 lg:mb-20"
          >
            <motion.span
              initial={{ opacity: 0, letterSpacing: '0.05em' }}
              animate={inViewFeatures ? { opacity: 1, letterSpacing: '0.2em' } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-teal text-xs sm:text-sm font-semibold uppercase tracking-wider mb-6"
            >
              /ÜRETİM ALTYAPISI
            </motion.span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mt-4 sm:mt-6 mb-4 sm:mb-6">
              Entegre Üretim Yapımız
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Tüm süreçler tek çatı altında, kontrollü ve verimli üretim
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {productionFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50, rotateX: -15 }}
                animate={inViewFeatures ? { opacity: 1, y: 0, rotateX: 0 } : {}}
                transition={{
                  duration: 0.7,
                  delay: index * 0.12,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
                className="group relative"
              >
                <div className="relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-teal/30 hover:shadow-2xl hover:-translate-y-3 transition-all duration-500 cursor-default overflow-hidden h-full">
                  {/* Top Gradient Line */}
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                  {/* Shimmer Effect */}
                  <div className="absolute inset-0 overflow-hidden rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <motion.div
                      animate={{ x: ['-100%', '200%'] }}
                      transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 2 }}
                      className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-transparent via-teal/5 to-transparent skew-x-12"
                    />
                  </div>

                  <div className="relative">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 8 }}
                      transition={{ duration: 0.4 }}
                      className={`w-14 h-14 flex items-center justify-center bg-gradient-to-br ${feature.color} rounded-xl text-white mb-6 shadow-lg`}
                    >
                      <i className={`${feature.icon} text-2xl`} />
                    </motion.div>

                    <h3 className="text-xl font-bold text-navy mb-3 group-hover:text-teal transition-colors duration-300">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                  </div>

                  {/* Bottom Accent */}
                  <div className={`absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r ${feature.color} scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CNC Workshop Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-teal/10 rounded-full text-teal text-xs font-semibold uppercase tracking-wider mb-6">
                <i className="ri-tools-line" />
                CNC Kalıp Atölyesi
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-6 leading-tight">
                Hızlı ve Hassas
                <br />
                <span className="bg-gradient-to-r from-teal to-green bg-clip-text text-transparent">
                  Kalıp Üretimi
                </span>
              </h2>
              <p className="text-gray-600 text-base lg:text-lg leading-relaxed mb-8">
                CNC kalıp atölyemiz sayesinde, ürün geliştirme ve revizyon süreçleri hızlı ve ölçülebilir 
                şekilde yürütülmekte; kalıp hassasiyeti ve üretim sürekliliği güvence altına alınmaktadır.
              </p>

              <div className="space-y-5">
                {[
                  { title: 'Hızlı Ürün Geliştirme', desc: 'Müşteri taleplerine hızlı yanıt ve prototip üretimi' },
                  { title: 'Mikron Hassasiyeti', desc: 'CNC teknolojisi ile hassas kalıp üretimi' },
                  { title: 'Kesintisiz Üretim', desc: 'Dışa bağımlılık olmadan sürekli üretim' },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                    className="flex items-start gap-4 group"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-green/10 rounded-lg group-hover:bg-green/20 transition-colors duration-300 flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-green text-xl" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-navy mb-1 group-hover:text-teal transition-colors duration-300">
                        {item.title}
                      </h4>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative w-full h-[450px] lg:h-[550px] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/bd4d812bda341cd02dd671d6f3d81fda.webp"
                  alt="CNC Kalıp Atölyesi"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/40 via-transparent to-transparent" />
              </div>

              {/* Floating Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl border border-gray-100"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-teal to-green rounded-xl">
                    <i className="ri-precision-line text-white text-2xl" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-navy">±0.01mm</div>
                    <div className="text-gray-500 text-sm">Hassasiyet</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Production Process */}
      <section
        ref={refProcess}
        id="uretim-sureci"
        className="py-20 lg:py-28 bg-white relative overflow-hidden"
      >
        <motion.div
          animate={{ x: [0, -20, 0], y: [0, 15, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-20 right-10 w-80 h-80 bg-green/5 rounded-full blur-3xl pointer-events-none"
        />

        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inViewProcess ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-14 lg:mb-20"
          >
            <span className="text-teal text-xs sm:text-sm font-semibold uppercase tracking-wider">
              /ÜRETİM SÜRECİ
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mt-4 sm:mt-6 mb-4 sm:mb-6">
              Üretimden Teslimat&apos;a
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Her aşamada titizlik, her üründe mükemmellik
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {productionSteps.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={inViewProcess ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
                className="group relative"
              >
                <div className="relative bg-white p-6 sm:p-8 rounded-2xl border border-gray-100 hover:border-teal/30 hover:shadow-xl hover:-translate-y-2 transition-all duration-500 cursor-default overflow-hidden">
                  {/* Step Number Background */}
                  <div className="absolute top-4 right-4 text-6xl sm:text-7xl font-bold text-gray-50 group-hover:text-teal/8 transition-colors duration-500 select-none leading-none">
                    {item.number}
                  </div>

                  {/* Hover Shimmer */}
                  <div className="absolute inset-0 overflow-hidden rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <motion.div
                      animate={{ x: ['-100%', '200%'] }}
                      transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 2 }}
                      className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-transparent via-teal/5 to-transparent skew-x-12"
                    />
                  </div>

                  <div className="relative">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 8 }}
                      transition={{ duration: 0.4 }}
                      className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-gradient-to-br from-teal to-green rounded-xl text-white mb-5 shadow-md"
                    >
                      <i className={`${item.icon} text-xl sm:text-2xl`} />
                    </motion.div>

                    <div className="text-teal text-xs font-bold mb-2 tracking-wider">
                      ADIM {item.number}
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-navy mb-2 group-hover:text-teal transition-colors duration-300">
                      {item.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>

                  {/* Bottom Accent */}
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-teal via-green to-teal scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Control Section */}
      <section
        ref={refQuality}
        id="kalite-kontrol"
        className="py-20 lg:py-28 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden"
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inViewQuality ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-14 lg:mb-20"
          >
            <span className="text-teal text-xs sm:text-sm font-semibold uppercase tracking-wider">
              /KALİTE KONTROL
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mt-4 sm:mt-6 mb-4 sm:mb-6">
              Kalite Kontrol Sistemimiz
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Üretim sürecinin her aşamasına entegre kalite güvencesi
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-12">
            {qualityControls.map((control, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                animate={inViewQuality ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                className="group"
              >
                <div className="bg-white rounded-2xl p-6 lg:p-8 border border-gray-100 hover:border-teal/30 hover:shadow-xl hover:-translate-y-2 transition-all duration-500 cursor-default h-full">
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-teal/10 to-green/10 rounded-xl mb-5 group-hover:from-teal/20 group-hover:to-green/20 transition-colors duration-300">
                    <i className={`${control.icon} text-teal text-2xl`} />
                  </div>
                  <h3 className="text-lg font-bold text-navy mb-2 group-hover:text-teal transition-colors duration-300">
                    {control.title}
                  </h3>
                  <p className="text-gray-600 text-sm">{control.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Info Box */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inViewQuality ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="bg-gradient-to-r from-teal/5 via-green/5 to-teal/5 rounded-2xl p-8 lg:p-10 text-center border border-teal/10"
          >
            <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-teal to-green rounded-xl mx-auto mb-5">
              <i className="ri-line-chart-line text-white text-2xl" />
            </div>
            <p className="text-gray-700 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto">
              Üretim kayıtları ile <strong className="text-navy">proses sürekliliği izlenir</strong> ve{' '}
              <strong className="text-navy">kalite standartlarının korunması</strong> sağlanır.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Lab Tests Section */}
      <section ref={refLab} className="py-20 lg:py-28 bg-white relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={inViewLab ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="relative order-2 lg:order-1"
            >
              <div className="relative w-full h-[450px] lg:h-[550px] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/1a4013147d78b8874224d285a55a078d.jpg"
                  alt="Kalite Kontrol Laboratuvarı"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/40 via-transparent to-transparent" />
              </div>

              {/* Floating Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inViewLab ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-5 shadow-xl border border-gray-100"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-teal to-green rounded-xl">
                    <i className="ri-test-tube-line text-white text-2xl" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-navy">100%</div>
                    <div className="text-gray-500 text-sm">Test Oranı</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={inViewLab ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="order-1 lg:order-2"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-teal/10 rounded-full text-teal text-xs font-semibold uppercase tracking-wider mb-6">
                <i className="ri-microscope-line" />
                Laboratuvar Testleri
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-6 leading-tight">
                Detaylı Test ve
                <br />
                <span className="bg-gradient-to-r from-teal to-green bg-clip-text text-transparent">
                  Analiz Süreçleri
                </span>
              </h2>
              <p className="text-gray-600 text-base lg:text-lg leading-relaxed mb-8">
                Her üretim partisinde kapsamlı fiziksel ve kimyasal testler uygulanarak 
                ürün kalitesi güvence altına alınmaktadır.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {labTests.map((test, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inViewLab ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                    className="group bg-gray-50 rounded-xl p-5 hover:bg-teal/5 hover:shadow-lg transition-all duration-300 cursor-default"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg shadow-sm group-hover:shadow-md transition-shadow duration-300 flex-shrink-0">
                        <i className={`${test.icon} text-teal text-xl`} />
                      </div>
                      <div>
                        <h4 className="text-base font-bold text-navy mb-1 group-hover:text-teal transition-colors duration-300">
                          {test.title}
                        </h4>
                        <p className="text-gray-500 text-xs">{test.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={refCTA} className="py-20 lg:py-28 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
        <motion.div
          animate={{ x: [0, 20, 0], y: [0, -15, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-20 right-20 w-72 h-72 bg-teal/5 rounded-full blur-3xl pointer-events-none"
        />

        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inViewCTA ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-navy via-navy to-navy/95 rounded-3xl p-10 lg:p-16 text-center relative overflow-hidden"
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-full h-full" style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '40px 40px'
              }} />
            </div>

            {/* Glowing Orbs */}
            <motion.div
              animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute top-10 left-10 w-32 h-32 bg-green/20 rounded-full blur-3xl"
            />
            <motion.div
              animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute bottom-10 right-10 w-40 h-40 bg-teal/20 rounded-full blur-3xl"
            />

            <div className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inViewCTA ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-green to-teal rounded-2xl mx-auto mb-8 shadow-lg shadow-green/30"
              >
                <i className="ri-phone-line text-white text-3xl" />
              </motion.div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Üretim Kapasitenizi
                <br />
                <span className="bg-gradient-to-r from-green via-teal to-green bg-clip-text text-transparent">
                  Birlikte Planlayalım
                </span>
              </h2>
              <p className="text-white/70 text-base lg:text-lg max-w-2xl mx-auto mb-10">
                Kalite standartlarımız ve üretim kapasitemiz hakkında detaylı bilgi almak için 
                bizimle iletişime geçin.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="/contact"
                  className="group relative px-10 py-4 bg-gradient-to-r from-green to-teal text-white font-semibold rounded-xl overflow-hidden transition-all duration-500 shadow-lg shadow-green/25 hover:shadow-xl hover:shadow-green/30 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    Teklif Alın
                    <motion.i
                      animate={{ x: [0, 4, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                      className="ri-arrow-right-line text-lg"
                    />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-teal to-green opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </a>
                <a
                  href="/products"
                  className="px-10 py-4 bg-white/10 border border-white/30 text-white font-semibold rounded-xl hover:bg-white/20 hover:border-white/50 transition-all duration-500 whitespace-nowrap cursor-pointer backdrop-blur-sm"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-box-3-line" />
                    Ürünlerimiz
                  </span>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductionQualityPage;
